export const initialState = {
  isDarkMode: false,
  mainLayout: "app",
  theme: "light",
  menu: "horizontal",
  layout: "full",
  rtlClass: "ltr",
  animation: "",
  navbar: "navbar-sticky",
  locale: "en",
  sidebar: false,
  languageList: [
    { code: "zh", name: "Chinese" },
    { code: "da", name: "Danish" },
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "el", name: "Greek" },
    { code: "hu", name: "Hungarian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "ru", name: "Russian" },
    { code: "es", name: "Spanish" },
    { code: "sv", name: "Swedish" },
    { code: "tr", name: "Turkish" },
    { code: "ae", name: "Arabic" },
  ],
  isShowMainLoader: true,
  semidark: false,
};

export type IndexState = typeof initialState;

function resolveIsDarkMode(theme: string, fallback: boolean): boolean {
  if (theme === "light") {
    return false;
  }

  if (theme === "dark") {
    return true;
  }

  if (theme === "system") {
    return !!globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches;
  }

  return fallback;
}

function handleToggleDirection(state: IndexState, payload: any) {
  localStorage.setItem("direction", payload);
  document.querySelector("html")?.setAttribute("dir", payload || "ltr");
  return { ...state, direction: payload };
}

function handleToggleTheme(state: IndexState, payload: any) {
  const theme = payload || state.theme;
  localStorage.setItem("theme", theme);
  const isDarkMode = resolveIsDarkMode(theme, !!state.isDarkMode);
  document.querySelector("body")?.classList.toggle("dark", isDarkMode);
  return { ...state, theme, isDarkMode };
}

function handleToggleMenu(state: IndexState, payload: any) {
  const menu = payload || state.menu;
  localStorage.setItem("menu", menu);
  return { ...state, sidebar: false, menu };
}

function handleToggleLayout(state: IndexState, payload: any) {
  const layout = payload || state.layout;
  localStorage.setItem("layout", layout);
  return { ...state, layout };
}

function handleToggleRTL(state: IndexState, payload: any) {
  const rtlClass = payload || state.rtlClass;
  localStorage.setItem("rtlClass", rtlClass);
  document.querySelector("html")?.setAttribute("dir", rtlClass || "ltr");
  return { ...state, rtlClass };
}

function handleToggleAnimation(state: IndexState, payload: any) {
  const animation = payload?.trim();
  localStorage.setItem("animation", animation);
  if (animation) {
    const eleanimation: any = document.querySelector(".animation");
    eleanimation?.classList.add("animate__animated", animation);
  }
  return { ...state, animation };
}

function handleToggleNavbar(state: IndexState, payload: any) {
  const navbar = payload || state.navbar;
  localStorage.setItem("navbar", navbar);
  return { ...state, navbar };
}

function handleToggleSemidark(state: IndexState, payload: any) {
  const semidark = payload || false;
  localStorage.setItem("semidark", semidark);
  return { ...state, semidark };
}

function handleToggleLocale(state: IndexState, payload: any) {
  const locale = payload || state.locale;
  localStorage.setItem("i18n_locale", locale);
  return { ...state, locale };
}

const indexActionHandlers: Record<
  string,
  (state: IndexState, payload: any) => IndexState
> = {
  toggleDirection: handleToggleDirection,
  toggleMainLoader: (state, payload) => ({ ...state, isShowMainLoader: payload }),
  setMainLayout: (state, payload) => ({ ...state, mainLayout: payload }),
  toggleTheme: handleToggleTheme,
  toggleMenu: handleToggleMenu,
  toggleLayout: handleToggleLayout,
  toggleRTL: handleToggleRTL,
  toggleAnimation: handleToggleAnimation,
  toggleNavbar: handleToggleNavbar,
  toggleSemidark: handleToggleSemidark,
  toggleLocale: handleToggleLocale,
  toggleSidebar: (state) => ({ ...state, sidebar: !state.sidebar }),
};

export function indexReducer(state = initialState, action: any) {
  const handler = indexActionHandlers[action.type];
  return handler ? handler(state, action.payload) : state;
}
